import React from 'react';
import Modal from '../Modal';
import { 
  CubeIcon, 
  ScaleIcon, 
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { Ruler } from 'lucide-react';

interface Packaging {
  id: number;
  nom: string;
  type: string;
  capacite: number;
  unite: string;
  dimensions: string;
  poids_tare: number;
  statut: string;
  created_at: string;
  updated_at: string;
}

interface PackagingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  packaging: Packaging;
}

const PackagingDetailsModal: React.FC<PackagingDetailsModalProps> = ({ isOpen, onClose, packaging }) => {
  const getTypeDisplay = (type: string) => {
    const types: { [key: string]: string } = {
      'sac': 'Sac',
      'carton': 'Carton',
      'bidon': 'Bidon',
      'palette': 'Palette',
      'autre': 'Autre'
    };
    return types[type] || type;
  };

  const getUniteDisplay = (unite: string) => {
    const unites: { [key: string]: string } = {
      'kg': 'Kilogrammes (kg)',
      'g': 'Grammes (g)',
      'l': 'Litres (l)',
      'ml': 'Millilitres (ml)',
      'pieces': 'Pièces'
    };
    return unites[unite] || unite;
  };

  const getStatusDisplay = (statut: string) => {
    return statut === 'actif' ? 'Actif' : 'Inactif';
  };

  const getStatusIcon = (statut: string) => {
    return statut === 'actif' ? (
      <CheckCircleIcon className="w-5 h-5 text-green-600" />
    ) : (
      <XCircleIcon className="w-5 h-5 text-red-600" />
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Détails du conditionnement" size="lg">
      <div className="space-y-6">
        {/* En-tête avec nom et statut */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
                         <div className="p-2 bg-orange-100 rounded-lg">
               <CubeIcon className="w-6 h-6 text-orange-600" />
             </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{packaging.nom}</h3>
              <p className="text-sm text-gray-600">ID: {packaging.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusIcon(packaging.statut)}
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              packaging.statut === 'actif' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {getStatusDisplay(packaging.statut)}
            </span>
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Type et Capacité */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type d'emballage</label>
                             <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                 <CubeIcon className="w-5 h-5 text-blue-600" />
                 <span className="text-sm font-medium text-blue-800">
                   {getTypeDisplay(packaging.type)}
                 </span>
               </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Capacité</label>
              <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
                <ScaleIcon className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  {packaging.capacite} {getUniteDisplay(packaging.unite)}
                </span>
              </div>
            </div>
          </div>

          {/* Dimensions et Tare */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dimensions</label>
              <div className="flex items-center space-x-2 p-3 bg-purple-50 rounded-lg">
                <Ruler className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">
                  {packaging.dimensions || 'Non définies'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Poids de tare</label>
              <div className="flex items-center space-x-2 p-3 bg-orange-50 rounded-lg">
                <ScaleIcon className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">
                  {packaging.poids_tare ? `${packaging.poids_tare} kg` : 'Non défini'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Informations supplémentaires */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Informations système</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Créé le:</span>
              <span className="text-gray-900">
                {packaging.created_at ? new Date(packaging.created_at).toLocaleDateString('fr-FR') : 'N/A'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Modifié le:</span>
              <span className="text-gray-900">
                {packaging.updated_at ? new Date(packaging.updated_at).toLocaleDateString('fr-FR') : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Bouton de fermeture */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PackagingDetailsModal;
